import { ColumnDef, createColumnHelper, Row } from '@tanstack/react-table';
import { useContext } from 'react';
import { addImageNotAvailable, buildApiImageUrl } from 'services/product-data.service';
import { TenantConfig, TenantConfigContext } from 'context/TenantConfigContext';
import { useModelService, Measure } from 'services/model/model.service';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import { DrillDownTableDateGmvValues } from 'pages/trading/outlier/DrillDownTableDateRangeRow';
import { buildDrillDownTableCoreColumns } from 'pages/trading/outlier/DrilldownCoreColumns';
import { useLocation, useNavigate } from 'react-router-dom';
import { MessageSentiment } from 'pages/trading/model';
import { TRADING_ROOT } from 'pages/trading/Trading';
import { DataTableContextProvider } from 'components/table/context/DataTableContextProvider';
import { DataTable } from 'components/table/DataTable';
import { useCoreDrilldownExportConfigOptions } from 'pages/trading/outlier/DrilldownExportUtil';

export interface ProductRow extends DrillDownTableDateGmvValues {
  artImageUrl: string;
  prdName: string;
  prdNumber: string;
}

const columnHelper = createColumnHelper<ProductRow>();
const buildColumns = (tenantContext: TenantConfig, headers: string[], intl: IntlShape, measure: Measure) => [
  columnHelper.accessor('artImageUrl', {
    enableSorting: false,
    header: () => <FormattedMessage id="general.label.image" />,
    cell: info => (
      <div className="">
        <img
          src={buildApiImageUrl(tenantContext.accountNumber, info.getValue())}
          onError={addImageNotAvailable}
          alt="Product Thumbnail"
          className="w-[48px]"
        />
      </div>
    ),
  }),
  columnHelper.accessor(row => row.prdNumber, {
    id: 'prdNumber',
    header: () => headers[0],
    cell: info => info.getValue(),
  }),
  columnHelper.accessor('prdName', {
    id: 'prdName',
    header: () => headers[1],
    cell: info => info.getValue(),
  }),
  ...buildDrillDownTableCoreColumns(intl, measure),
];

export interface ProductsTableProps {
  rows: ProductRow[];
  measure: Measure;
  sentiment?: MessageSentiment;
  detectionDate?: string;
}

export function ProductsTable({ rows, measure, sentiment = 'NEUTRAL', detectionDate = undefined }: ProductsTableProps) {
  const { getAttributes } = useModelService();
  const [prdNumber, prdName] = getAttributes(['prdNumber', 'prdName']);

  const tenantContext = useContext(TenantConfigContext);
  const intl = useIntl();
  const navigate = useNavigate();
  const location = useLocation();

  const onRowClick = (row: Row<unknown>) => {
    const castedRow = row as Row<ProductRow>;
    navigate(`${TRADING_ROOT}/drilldown/prdNumber/${castedRow.original.prdNumber}${location.search}`);
  };

  const columns = buildColumns(tenantContext, [prdNumber.name, prdName.name], intl, measure) as ColumnDef<unknown>[];

  const coreDrilldownExportConfigOptions = useCoreDrilldownExportConfigOptions('products');

  return (
    <DataTableContextProvider>
      <DataTable
        testId="trading-drilldown-products-table"
        titleId="insights.products"
        subTitleDecorator={header =>
          `${header}${detectionDate ? ` ${intl.formatMessage({ id: 'trading.drilldown.detection.date' })} ${detectionDate}` : ''}`
        }
        rows={rows}
        columns={columns}
        onRowClick={onRowClick}
        exportConfigOptions={coreDrilldownExportConfigOptions}
        defaultSortingState={[{ id: 'onDayComparisonGmv', desc: sentiment !== 'NEGATIVE' }]}
      />
    </DataTableContextProvider>
  );
}
